import type {
  Channel,
  Video,
  VideosResponse,
  WatchStatus,
  AddChannelRequest,
  UpdateChannelRequest,
  MarkWatchedRequest,
  WatchLimitStatus,
} from '../types';

const API_BASE = '/api';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchAPI<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const password = localStorage.getItem('tubemeup_auth_password');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string>),
  };

  if (password) {
    headers['X-Auth-Password'] = password;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.error || 'Request failed');
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return null as T;
  }

  return response.json();
}

// ============ CHANNELS ============

export const channelsApi = {
  async getAll(): Promise<Channel[]> {
    return fetchAPI<Channel[]>('/channels');
  },

  async getById(id: string): Promise<Channel> {
    return fetchAPI<Channel>(`/channels/${id}`);
  },

  async add(data: AddChannelRequest): Promise<Channel> {
    return fetchAPI<Channel>('/channels', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async update(id: string, data: UpdateChannelRequest): Promise<Channel> {
    return fetchAPI<Channel>(`/channels/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async delete(id: string): Promise<void> {
    return fetchAPI<void>(`/channels/${id}`, {
      method: 'DELETE',
    });
  },
};

// ============ VIDEOS ============

export interface GetVideosParams {
  filter?: 'all' | 'unwatched' | 'watchlist';
  daysBack?: number;
  offset?: number;
  limit?: number;
}

export const videosApi = {
  async getAll(params?: GetVideosParams): Promise<VideosResponse> {
    const queryParams = new URLSearchParams();

    if (params?.filter) queryParams.set('filter', params.filter);
    if (params?.daysBack !== undefined) queryParams.set('daysBack', params.daysBack.toString());
    if (params?.offset !== undefined) queryParams.set('offset', params.offset.toString());
    if (params?.limit !== undefined) queryParams.set('limit', params.limit.toString());

    const query = queryParams.toString();
    return fetchAPI<VideosResponse>(`/videos${query ? `?${query}` : ''}`);
  },

  async getById(id: string): Promise<Video> {
    return fetchAPI<Video>(`/videos/${id}`);
  },
};

// ============ WATCH STATUS ============

export const watchStatusApi = {
  async get(videoId: string): Promise<WatchStatus> {
    return fetchAPI<WatchStatus>(`/watch-status/${videoId}`);
  },

  async markWatched(data: MarkWatchedRequest): Promise<WatchStatus> {
    return fetchAPI<WatchStatus>('/watch-status', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async unmark(videoId: string): Promise<void> {
    return fetchAPI<void>(`/watch-status/${videoId}`, {
      method: 'DELETE',
    });
  },
};

// ============ POLLING ============

export interface PollResponse {
  success: boolean;
  message: string;
  totalNew: number;
  channels?: Array<{
    channelId: string;
    success: boolean;
    newVideos: number;
    totalVideos: number;
  }>;
}

export interface PollProgress {
  completed: number;
  total: number;
  channelId: string;
}

export const pollApi = {
  async pollAll(daysBack: number = 7): Promise<PollResponse> {
    return fetchAPI<PollResponse>('/poll', {
      method: 'POST',
      body: JSON.stringify({ daysBack }),
    });
  },

  pollAllStream(
    daysBack: number = 7,
    onTotal: (total: number) => void,
    onProgress: (progress: PollProgress) => void,
    onDone: (result: PollResponse) => void,
    onError: (error: string) => void,
  ): () => void {
    const password = localStorage.getItem('tubemeup_auth_password');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (password) headers['X-Auth-Password'] = password;

    let stopped = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let totalReported = false;

    const pollProgress = () => {
      if (stopped) return;
      fetch(`${API_BASE}/poll/stream`, { headers })
        .then(r => r.json())
        .then((data: { status: string; completed?: number; total?: number }) => {
          if (stopped) return;
          if (data.status === 'running') {
            if (!totalReported && data.total !== undefined) {
              onTotal(data.total);
              totalReported = true;
            }
            if (data.completed !== undefined && data.total !== undefined) {
              onProgress({ completed: data.completed, total: data.total, channelId: '' });
            }
            pollTimer = setTimeout(pollProgress, 500);
          }
          // if idle the POST has finished — onDone will be called from the POST promise
        })
        .catch(() => {
          if (!stopped) pollTimer = setTimeout(pollProgress, 500);
        });
    };

    // Start the poll job
    fetch(`${API_BASE}/poll/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ daysBack }),
    })
      .then(r => r.json())
      .then((result: PollResponse) => {
        if (stopped) return;
        stopped = true;
        if (pollTimer) clearTimeout(pollTimer);
        onDone(result);
      })
      .catch(() => {
        if (!stopped) {
          stopped = true;
          if (pollTimer) clearTimeout(pollTimer);
          onError('Failed to fetch new videos from YouTube');
        }
      });

    // Start polling for progress after a brief delay
    pollTimer = setTimeout(pollProgress, 300);

    return () => {
      stopped = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  },

  async pollChannel(channelId: string, daysBack: number = 7): Promise<PollResponse> {
    return fetchAPI<PollResponse>(`/poll/${channelId}`, {
      method: 'POST',
      body: JSON.stringify({ daysBack }),
    });
  },
};

// ============ WATCHLIST ============

export interface WatchlistEntry {
  addedAt: string;
}

export const watchlistApi = {
  async getAll(): Promise<Record<string, WatchlistEntry>> {
    return fetchAPI<Record<string, WatchlistEntry>>('/watchlist');
  },

  async isInWatchlist(videoId: string): Promise<boolean> {
    const result = await fetchAPI<{ inWatchlist: boolean }>(`/watchlist/${videoId}`);
    return result.inWatchlist;
  },

  async add(videoId: string): Promise<WatchlistEntry> {
    return fetchAPI<WatchlistEntry>('/watchlist', {
      method: 'POST',
      body: JSON.stringify({ videoId }),
    });
  },

  async remove(videoId: string): Promise<void> {
    return fetchAPI<void>(`/watchlist/${videoId}`, {
      method: 'DELETE',
    });
  },
};

// ============ WATCH LIMIT ============

export const watchLimitApi = {
  async getStatus(): Promise<WatchLimitStatus> {
    return fetchAPI<WatchLimitStatus>('/watch-limit');
  },

  async activate(type: 'weekly' | 'extra' | 'auto' = 'auto'): Promise<WatchLimitStatus> {
    return fetchAPI<WatchLimitStatus>('/watch-limit/activate', {
      method: 'POST',
      body: JSON.stringify({ type }),
    });
  },
};

export { ApiError };
